import { type Job, JobState, PiccoloError } from '@piccolohealth/pbs-common';
import { Async } from '@piccolohealth/util';
import React from 'react';
import type { MutationFn } from '../graphql/fetcher';
import { useExportIncidentsMutation } from '../graphql/hooks/useExportIncidentsMutation';
import { useExportParticipantMutation } from '../graphql/hooks/useExportParticipantMutation';
import { useExportParticipantsMutation } from '../graphql/hooks/useExportParticipantsMutation';
import { useJobQuery } from '../graphql/hooks/useJobQuery';
import { useScoreParticipantDocumentMutation } from '../graphql/hooks/useScoreParticipantDocumentMutation';
import type {
  ExportIncidentsMutationVariables,
  ExportParticipantMutationVariables,
  ExportParticipantsMutationVariables,
  ScoreParticipantDocumentMutationVariables,
} from '../graphql/types';
import { asPiccoloError } from '../utils/errors';
import { useAppContext } from './useAppContext';

const POLL_INTERVAL = 2000;
const MAX_POLL_ATTEMPTS = 150; // Try for 5m at 2s intervals

const IN_FLIGHT_STATUSES: JobState[] = [JobState.Created, JobState.Retry, JobState.Active];
const FAILED_STATUSES: JobState[] = [JobState.Failed, JobState.Cancelled];

export interface UseJobOptions<A, B> {
  mutationFn: MutationFn<A, B>;
  parseResponse: (response: A) => Job;
}

export interface UseJobResponse<B> {
  // Always resolves with a job in a terminal state, or null if never reached one.
  // Will never throw/reject as we want the caller to read from the job.
  start: (variables: B) => Promise<Job | null>;
  isLoading: boolean;
  isCompleted: boolean;
  job: Job | null;
  error: PiccoloError | null;
}

const useJob = <A, B>(options: UseJobOptions<A, B>): UseJobResponse<B> => {
  const { mutationFn, parseResponse } = options;
  const { organization } = useAppContext();
  const [job, setJob] = React.useState<Job | null>(null);
  const [error, setError] = React.useState<PiccoloError | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const mutation = mutationFn({});
  const fetchJob = useJobQuery.getFetcher();

  const isCompleted = job?.state === JobState.Completed;

  const start = async (variables: B): Promise<Job | null> => {
    setJob(null);
    setError(null);
    setIsLoading(true);

    try {
      const createdJob = parseResponse(await mutation.mutateAsync(variables));
      setJob(createdJob);

      const finalJob = await Async.pollUntil(
        async () => {
          const data = await fetchJob({ organizationId: organization.id, jobId: createdJob.id });
          const polledJob = data.organization?.job ?? null;
          setJob(polledJob);

          return polledJob;
        },
        (polledJob) => !polledJob || !IN_FLIGHT_STATUSES.includes(polledJob.state),
        POLL_INTERVAL,
        MAX_POLL_ATTEMPTS,
      );

      if (finalJob && FAILED_STATUSES.includes(finalJob.state)) {
        setError(
          new PiccoloError({
            type: finalJob.response?.type ?? 'InternalServerError',
            message: finalJob.response?.message ?? 'The job did not complete',
            metadata: finalJob.response?.metadata,
          }),
        );
      }

      return finalJob;
    } catch (e) {
      setError(asPiccoloError(e));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    start,
    isLoading,
    isCompleted,
    job,
    error,
  };
};

export type UseParticipantExportJobResponse = UseJobResponse<ExportParticipantMutationVariables>;

export const useParticipantExportJob = (): UseParticipantExportJobResponse => {
  return useJob({
    mutationFn: useExportParticipantMutation,
    parseResponse: (result) => result.exportParticipant,
  });
};

export type UseParticipantsExportJobResponse = UseJobResponse<ExportParticipantsMutationVariables>;

export const useParticipantsExportJob = (): UseParticipantsExportJobResponse => {
  return useJob({
    mutationFn: useExportParticipantsMutation,
    parseResponse: (result) => result.exportParticipants,
  });
};

export type UseIncidentsExportJobResponse = UseJobResponse<ExportIncidentsMutationVariables>;

export const useIncidentsExportJob = (): UseIncidentsExportJobResponse => {
  return useJob({
    mutationFn: useExportIncidentsMutation,
    parseResponse: (result) => result.exportIncidents,
  });
};

export type UseScoreParticipantDocumentJobResponse =
  UseJobResponse<ScoreParticipantDocumentMutationVariables>;

export const useScoreParticipantDocumentJob = (): UseScoreParticipantDocumentJobResponse => {
  return useJob({
    mutationFn: useScoreParticipantDocumentMutation,
    parseResponse: (result) => result.scoreParticipantDocument,
  });
};
